import { OverlapTransitionStateContext } from "@redotech/react-animation/outlet-transition";
import { OverlapTransitionState } from "@redotech/react-animation/transition";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
} from "react";
import type {
  Breadcrumb,
  BreadcrumbSize,
  BreadcrumbWeight,
} from "./breadcrumb";
import type {
  PageAction,
  PageLeadingVisual,
  PageSubtitle,
  PageTab,
} from "./page-prop-types";
import type { BgColorValue } from "./theme/box";

// Leaves call these hooks to register header props on the singleton `<Page>`
// up at the app shell. Array/object values must be memoized — they're
// `useLayoutEffect` deps.

interface PageHeaderSlotSetters {
  setTitle: (value: string | undefined) => void;
  setTitleActions: (value: ReactNode | undefined) => void;
  setTitleTrailing: (value: ReactNode | undefined) => void;
  setSubtitle: (value: PageSubtitle | undefined) => void;
  setBreadcrumbs: (value: Breadcrumb[] | undefined) => void;
  setLeadingVisual: (value: PageLeadingVisual | undefined) => void;
  setActions: (value: PageAction[] | undefined) => void;
  setTabs: (value: PageTab[] | undefined) => void;
  setHideTitle: (value: boolean) => void;
  setSurface: (value: PageHeaderSurface | undefined) => void;
}

/** A header that continues the page body's surface, with no divider under it. */
export interface PageHeaderSurface {
  divider: boolean;
  bgColor: BgColorValue;
  /** The trail's size; the header's default is xs. */
  breadcrumbSize?: BreadcrumbSize;
  /** The trail's weight; the header's default is semibold. */
  breadcrumbWeight?: BreadcrumbWeight;
  /** Keep a last crumb that repeats the title; the header drops it by default. */
  keepTrailingBreadcrumb?: boolean;
}

const NOOP_SETTERS: PageHeaderSlotSetters = {
  setTitle: () => {},
  setTitleActions: () => {},
  setTitleTrailing: () => {},
  setSubtitle: () => {},
  setBreadcrumbs: () => {},
  setLeadingVisual: () => {},
  setActions: () => {},
  setTabs: () => {},
  setHideTitle: () => {},
  setSurface: () => {},
};

export const PageHeaderSlotContext =
  createContext<PageHeaderSlotSetters>(NOOP_SETTERS);

function usePageHeaderSlot<T>(
  value: T,
  setValue: (value: T) => void,
  cleanupValue: T,
): void {
  const transitionState = useContext(OverlapTransitionStateContext);

  useLayoutEffect(() => {
    if (transitionState === OverlapTransitionState.EXIT) {
      return undefined;
    }

    setValue(value);
    return () => setValue(cleanupValue);
  }, [cleanupValue, setValue, transitionState, value]);
}

export function usePageTitle(title: string | undefined): void {
  const { setTitle } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(title, setTitle, undefined);
}

export function usePageTitleActions(actions: ReactNode | undefined): void {
  const { setTitleActions } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(actions, setTitleActions, undefined);
}

/** Inline content right after the title, such as a count. */
export function usePageTitleTrailing(trailing: ReactNode | undefined): void {
  const { setTitleTrailing } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(trailing, setTitleTrailing, undefined);
}

export function usePageSubtitle(subtitle: PageSubtitle | undefined): void {
  const { setSubtitle } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(subtitle, setSubtitle, undefined);
}

export function usePageBreadcrumbs(
  breadcrumbs: Breadcrumb[] | undefined,
): void {
  const { setBreadcrumbs } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(breadcrumbs, setBreadcrumbs, undefined);
}

export function usePageLeadingVisual(
  leadingVisual: PageLeadingVisual | undefined,
): void {
  const { setLeadingVisual } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(leadingVisual, setLeadingVisual, undefined);
}

export function usePageActions(actions: PageAction[] | undefined): void {
  const { setActions } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(actions, setActions, undefined);
}

export function usePageTabs(tabs: PageTab[] | undefined): void {
  const { setTabs } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(tabs, setTabs, undefined);
}

export function useHidePageTitle(hide = true): void {
  const { setHideTitle } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(hide, setHideTitle, false);
}

export function usePageHeaderSurface(
  surface: PageHeaderSurface | undefined,
): void {
  const { setSurface } = useContext(PageHeaderSlotContext);
  usePageHeaderSlot(surface, setSurface, undefined);
}

export interface PageHeaderConfig {
  title?: string;
  titleActions?: ReactNode;
  subtitle?: PageSubtitle;
  breadcrumbs?: Breadcrumb[];
  leadingVisual?: PageLeadingVisual;
  actions?: PageAction[];
  tabs?: PageTab[];
}

export function usePageHeader(config: PageHeaderConfig): void {
  usePageTitle(config.title);
  usePageTitleActions(config.titleActions);
  usePageSubtitle(config.subtitle);
  usePageBreadcrumbs(config.breadcrumbs);
  usePageLeadingVisual(config.leadingVisual);
  usePageActions(config.actions);
  usePageTabs(config.tabs);
}
